// OGC-531 Creatives Manager v19 renovation types (TYPE-ORDERING.md,
// LIFECYCLE.md): add, rename, drag/keyboard/Move-menu reordering with Undo,
// archive, unarchive and delete with the server's usage and eligibility.
// The order is saved in one atomic call against the order it was made from;
// a failed save restores the previous order and offers Retry.
import { useEffect, useRef, useState } from 'react';
import Menu from '../../components/Menu.jsx';
import { notifyCuratedCatalogChanged } from '../../curated-catalog.js';
import CatalogDialog from './CatalogDialog.jsx';
import RenovationTypeDialog from './RenovationTypeDialog.jsx';
import { CmIcon } from './creatives-icons.jsx';
import { attemptKey, catalogErrorMessage } from './creatives-api.js';
import useRowReorder, { moveId, sameOrder } from './useRowReorder.js';

const REFERENCE_LABELS = [
  ['activeCreatives', 'active creative', 'active creatives'],
  ['archivedCreatives', 'archived creative', 'archived creatives'],
  ['versions', 'saved creative version', 'saved creative versions'],
  ['campaignSnapshots', 'campaign creative selection', 'campaign creative selections'],
  ['campaignConfigurations', 'campaign draft', 'campaign drafts'],
  ['campaignApprovals', 'campaign approval', 'campaign approvals'],
];

export function referenceLines(references = {}) {
  return REFERENCE_LABELS.flatMap(([key, one, many]) => {
    const count = Number(references[key]) || 0;
    return count > 0 ? [`${count} ${count === 1 ? one : many}`] : [];
  });
}

// Undo restores the earlier order of types that are still active; types added
// or unarchived since stay appended. Deleted or archived types are not revived.
export function undoOrder(previousIds, currentActiveIds) {
  const active = new Set(currentActiveIds);
  const restored = previousIds.filter((id) => active.has(id));
  return [...restored, ...currentActiveIds.filter((id) => !restored.includes(id))];
}

export default function RenovationTypesManager({ apiClient, onBack, onToast, onViewCreatives, onTypeDeleted }) {
  const [state, setState] = useState({ status: 'loading', types: [], error: null });
  const [dialog, setDialog] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);
  // Callbacks kept by the toast (Undo) and Retry read this, not a stale
  // render's `saving`, so two order saves never overlap.
  const savingRef = useRef(false);
  const [announcement, setAnnouncement] = useState('');
  const headingRef = useRef(null);
  const loadSequence = useRef(0);
  const handleRefs = useRef(new Map());
  const pendingFocus = useRef(null);

  async function load() {
    const sequence = ++loadSequence.current;
    setState((current) => ({ ...current, status: current.types.length ? 'refreshing' : 'loading', error: null }));
    try {
      const types = await apiClient.typeUsage();
      if (sequence !== loadSequence.current) return null;
      setState({ status: 'ready', types, error: null });
      return types;
    } catch (error) {
      if (sequence === loadSequence.current) setState((current) => ({ ...current, status: 'error', error }));
      return null;
    }
  }

  useEffect(() => {
    load();
    headingRef.current?.focus({ preventScroll: true });
    return () => { loadSequence.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeTypes = state.types.filter((type) => !type.archivedAt);
  const archivedTypes = state.types.filter((type) => type.archivedAt);
  const activeIds = activeTypes.map((type) => type.id);
  const byId = new Map(state.types.map((type) => [type.id, type]));

  // Return focus to a moved or restored type's grip, but only when focus
  // was lost to the page; never take it from something the person is using.
  useEffect(() => {
    const id = pendingFocus.current;
    if (!id) return;
    const handle = handleRefs.current.get(id);
    if (!handle?.isConnected) return;
    pendingFocus.current = null;
    const active = document.activeElement;
    if (!active || active === document.body) handle.focus({ preventScroll: true });
  });

  async function saveOrder(nextIds, movedId, beforeIds, { undo = false } = {}) {
    if (sameOrder(nextIds, beforeIds)) return;
    if (savingRef.current) {
      onToast('The type order is still saving. Try again in a moment.');
      return;
    }
    savingRef.current = true;
    setSaveError(null);
    setSaving(true);
    // Show the new order while it saves; restore it if the save fails.
    setState((current) => ({
      ...current,
      types: [...nextIds.map((id) => current.types.find((type) => type.id === id)).filter(Boolean), ...current.types.filter((type) => type.archivedAt)],
    }));
    pendingFocus.current = movedId;
    try {
      await apiClient.reorderTypes(nextIds, beforeIds, attemptKey('renovation-type-order', movedId || 'undo'));
      notifyCuratedCatalogChanged();
      savingRef.current = false;
      setSaving(false);
      pendingFocus.current = movedId;
      await load();
      if (undo) {
        onToast('Previous type order restored.');
      } else {
        onToast('Type order updated.', {
          label: 'Undo',
          onClick: () => {
            const latest = latestActiveIds.current;
            saveOrder(undoOrder(beforeIds, latest), movedId, latest, { undo: true });
          },
        });
      }
    } catch (error) {
      savingRef.current = false;
      setSaving(false);
      const stale = error?.code === 'catalog_renovation_type_order_stale';
      pendingFocus.current = movedId;
      const reloaded = await load();
      // A retry after a lost response finds its own order already saved.
      const savedOrder = (reloaded || []).filter((type) => !type.archivedAt).map((type) => type.id);
      if (stale && sameOrder(savedOrder, nextIds)) {
        notifyCuratedCatalogChanged();
        onToast(undo ? 'Previous type order restored.' : 'Type order updated.');
        return;
      }
      setSaveError({
        message: stale ? catalogErrorMessage(error) : 'The new type order could not be saved. The previous order is shown.',
        retry: stale ? null : () => saveOrder(nextIds, movedId, beforeIds, { undo }),
      });
    }
  }

  const latestActiveIds = useRef(activeIds);
  latestActiveIds.current = activeIds;

  const reorder = useRowReorder({
    ids: activeIds,
    onCommit: (nextIds, movedId, beforeIds) => saveOrder(nextIds, movedId, beforeIds),
    announce: setAnnouncement,
  });

  // While an order saves the grips stay focusable (aria-disabled) but ignore
  // presses, keys and clicks, so keyboard focus is not dropped.
  function blockWhileSaving(handlers, extra) {
    const blocked = (event) => {
      if (!savingRef.current) return false;
      event.preventDefault();
      event.stopPropagation();
      return true;
    };
    return {
      ...handlers,
      ...extra,
      'aria-disabled': saving ? 'true' : undefined,
      onPointerDown: (event) => { if (!blocked(event)) handlers.onPointerDown(event); },
      onKeyDown: (event) => {
        if (savingRef.current && [' ', 'Enter', 'ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) { blocked(event); return; }
        handlers.onKeyDown(event);
      },
      onClickCapture: (event) => { if (!blocked(event)) handlers.onClickCapture(event); },
    };
  }

  function moveMenuItems(type) {
    const index = activeIds.indexOf(type.id);
    const move = (to) => saveOrder(moveId(activeIds, type.id, to), type.id, activeIds);
    return [
      ...(index > 0 ? [{ id: 'up', label: 'Move up', onSelect: () => move(index - 1) }] : []),
      ...(index < activeIds.length - 1 ? [{ id: 'down', label: 'Move down', onSelect: () => move(index + 1) }] : []),
      ...(index > 1 ? [{ id: 'top', label: 'Move to top', separatorBefore: true, onSelect: () => move(0) }] : []),
      ...(index < activeIds.length - 2 ? [{ id: 'bottom', label: 'Move to bottom', separatorBefore: index <= 1, onSelect: () => move(activeIds.length - 1) }] : []),
    ];
  }

  function closeDialog() {
    setDialog(null);
  }

  async function runTypeAction(perform, success, { focusId = null } = {}) {
    setDialog((current) => ({ ...current, busy: true, error: '' }));
    try {
      await perform();
      notifyCuratedCatalogChanged();
      setDialog(null);
      if (focusId) pendingFocus.current = focusId;
      await load();
      success();
    } catch (error) {
      setDialog((current) => (current ? { ...current, busy: false, error: catalogErrorMessage(error) } : current));
      if (['catalog_renovation_type_in_use', 'catalog_renovation_type_referenced', 'catalog_renovation_type_not_found', 'catalog_renovation_type_archived'].includes(error?.code)) load();
    }
  }

  function requestArchive(type) {
    if (!type.canArchive) {
      setDialog({ kind: 'archive-blocked', type });
      return;
    }
    setDialog({ kind: 'archive', type, key: attemptKey('renovation-type-archive', type.id) });
  }

  function requestDelete(type) {
    setDialog({ kind: type.canDelete ? 'delete' : 'delete-blocked', type, key: attemptKey('renovation-type-delete', type.id) });
  }

  let dialogElement = null;
  if (dialog && ['add', 'rename'].includes(dialog.kind)) {
    dialogElement = (
      <RenovationTypeDialog
        open
        type={dialog.kind === 'rename' ? dialog.type : null}
        types={state.types}
        apiClient={apiClient}
        onClose={closeDialog}
        onSaved={async (saved, { created }) => {
          setDialog(null);
          await load();
          if (created) headingRef.current?.focus({ preventScroll: true });
          onToast(created ? 'Renovation type added.' : 'Renovation type updated.');
        }}
      />
    );
  } else if (dialog) {
    const { kind, type, busy, error } = dialog;
    const common = { open: true, busy, error: error || '', onClose: closeDialog };
    const viewCreatives = { label: 'View creatives', onClick: () => { setDialog(null); onViewCreatives(type.id); } };
    const lines = referenceLines(type.references);
    if (kind === 'archive-blocked') {
      const count = Number(type.references?.activeCreatives) || 0;
      dialogElement = (
        <CatalogDialog {...common} title="This type is still in use"
          description={`${type.displayName} is assigned to ${count} active creative${count === 1 ? '' : 's'} or saved draft${count === 1 ? '' : 's'}.`}
          actions={[{ label: 'Keep type', autoFocus: true, onClick: closeDialog }, viewCreatives]}
        >
          <p>Archive those creatives or move their saved drafts to another active type before archiving this type.</p>
        </CatalogDialog>
      );
    } else if (kind === 'archive') {
      dialogElement = (
        <CatalogDialog {...common} title="Archive this renovation type?"
          description={`${type.displayName} will no longer be available when adding or editing creatives.`}
          actions={[
            { label: 'Keep type', autoFocus: true, onClick: closeDialog },
            { label: 'Archive type', tone: 'danger', busyLabel: 'Archiving…', onClick: () => runTypeAction(
              () => apiClient.archiveType(type.id, dialog.key),
              () => { headingRef.current?.focus({ preventScroll: true }); onToast('Renovation type archived.'); },
            ) },
          ]}
        >
          <div className="notice">Archived creative history and existing campaigns stay unchanged. You can unarchive this type later.</div>
        </CatalogDialog>
      );
    } else if (kind === 'unarchive') {
      dialogElement = (
        <CatalogDialog {...common} title="Unarchive this renovation type?"
          description={`${type.displayName} will be available when adding or editing creatives again.`}
          actions={[
            { label: 'Keep archived', autoFocus: true, onClick: closeDialog },
            { label: 'Unarchive type', tone: 'confirm', busyLabel: 'Unarchiving…', onClick: () => runTypeAction(
              () => apiClient.unarchiveType(type.id, dialog.key),
              () => onToast('Renovation type unarchived.'),
              { focusId: type.id },
            ) },
          ]}
        >
          <div className="notice">It will be added to the end of your active types. Archived creatives stay archived.</div>
        </CatalogDialog>
      );
    } else if (kind === 'delete-blocked') {
      dialogElement = (
        <CatalogDialog {...common} title="This type can’t be deleted"
          description={`${type.displayName} is referenced by creatives, their saved history or campaigns.`}
          actions={[{ label: 'Keep type', autoFocus: true, onClick: closeDialog }, viewCreatives]}
        >
          <div className="dialog-description-summary">
            {lines.map((line) => <div key={line}>{line}</div>)}
          </div>
          <p>Only types with no creative or campaign references can be deleted. Archived creatives and earlier versions still count.</p>
        </CatalogDialog>
      );
    } else if (kind === 'delete') {
      dialogElement = (
        <CatalogDialog {...common} title="Delete this renovation type?"
          description="This permanently removes the type. It cannot be undone."
          actions={[
            { label: 'Keep type', autoFocus: true, onClick: closeDialog },
            { label: 'Delete type', tone: 'danger', busyLabel: 'Deleting…', onClick: () => runTypeAction(
              () => apiClient.deleteType(type.id, dialog.key),
              () => { onTypeDeleted?.(type.id); headingRef.current?.focus({ preventScroll: true }); onToast('Unused renovation type deleted.'); },
            ) },
          ]}
        >
          <div className="dialog-description-summary">{type.displayName}<small>No creative or campaign references.</small></div>
        </CatalogDialog>
      );
    }
  }

  const ordered = [...reorder.order.map((id) => byId.get(id)).filter(Boolean), ...archivedTypes];
  return (
    <>
      <button type="button" className="back-link" onClick={onBack}><CmIcon name="back" /> Back to creative catalog</button>
      <header className="management-page-heading">
        <div>
          <span className="management-eyebrow">Platform administration</span>
          <h1 ref={headingRef} tabIndex={-1}>Renovation types</h1>
          <p>Organize the types available when adding creatives.</p>
        </div>
        <div className="header-actions">
          <button type="button" className="management-primary" onClick={() => setDialog({ kind: 'add' })}>
            Add renovation type <span aria-hidden="true">&nbsp; +</span>
          </button>
        </div>
      </header>

      {saveError ? (
        <div className="notice page-notice" role="alert">
          <span>{saveError.message}</span>
          {saveError.retry ? <button type="button" onClick={saveError.retry}>Retry</button> : null}
        </div>
      ) : null}

      <div className="management-panel type-order-panel">
        <div className="type-order-hint">
          <CmIcon name="grip" size={18} />
          <span>Drag rows to change their order.</span>
          <span className="type-order-count">{activeTypes.length} active type{activeTypes.length === 1 ? '' : 's'}</span>
        </div>
        <p id="type-reorder-help" className="sr-only">Drag to reorder, or click a handle for move options. With a handle focused, press Space to pick up, arrow keys to move, Enter to drop, or Escape to cancel.</p>
        <div className="management-table-wrap">
          <table className="management-table type-order-table">
            <thead>
              <tr><th>Renovation type</th><th>Creatives</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {ordered.map((type) => {
                const archived = Boolean(type.archivedAt);
                const creatives = (Number(type.references?.activeCreatives) || 0) + (Number(type.references?.archivedCreatives) || 0);
                return (
                  <tr
                    key={type.id}
                    ref={archived ? undefined : reorder.rowRef(type.id)}
                    className={[archived ? 'is-archived' : '', reorder.active === type.id ? 'is-reordering' : '', saving ? 'is-saving' : ''].filter(Boolean).join(' ') || undefined}
                  >
                    <td data-label="Renovation type">
                      <div className="type-name-cell">
                        {archived ? <span className="type-drag-spacer" aria-hidden="true" /> : (
                          <Menu
                            label={`Reorder ${type.displayName}`}
                            icon={<CmIcon name="grip" size={18} />}
                            triggerClassName="type-drag-handle"
                            disabled={activeTypes.length < 2}
                            items={moveMenuItems(type)}
                            triggerProps={blockWhileSaving(reorder.handleProps(type.id, type.displayName), {
                              'aria-describedby': 'type-reorder-help',
                              title: 'Drag to reorder or click for move options',
                              'data-reorder-id': type.id,
                            })}
                            onTriggerRef={(element) => {
                              if (element) handleRefs.current.set(type.id, element);
                              else handleRefs.current.delete(type.id);
                            }}
                          />
                        )}
                        <div><strong>{type.displayName}</strong><small>{type.key}</small></div>
                      </div>
                    </td>
                    <td data-label="Creatives">
                      {creatives}
                      <span className="type-count">{Number(type.publishedCreatives) || 0} published</span>
                    </td>
                    <td data-label="Status">
                      <span className="management-status" data-status={archived ? 'archived' : 'active'}>{archived ? 'Archived' : 'Active'}</span>
                    </td>
                    <td data-label="Actions">
                      <div className="row-actions type-actions">
                        {archived
                          ? <button key="unarchive" type="button" onClick={() => setDialog({ kind: 'unarchive', type, key: attemptKey('renovation-type-unarchive', type.id) })}>Unarchive</button>
                          : <button key="rename" type="button" onClick={() => setDialog({ kind: 'rename', type })}>Rename</button>}
                        <Menu
                          label={`More actions for ${type.displayName}`}
                          items={[
                            archived
                              ? { id: 'unarchive', label: 'Unarchive type', icon: <CmIcon name="unarchive" size={18} />, onSelect: () => setDialog({ kind: 'unarchive', type, key: attemptKey('renovation-type-unarchive', type.id) }) }
                              : { id: 'archive', label: 'Archive type', icon: <CmIcon name="archive" size={18} />, onSelect: () => requestArchive(type) },
                            { id: 'delete', label: 'Delete type', icon: <CmIcon name="trash" size={18} />, tone: 'danger', separatorBefore: true, onSelect: () => requestDelete(type) },
                          ]}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
              {state.status === 'ready' && !ordered.length ? (
                <tr><td colSpan={4}><div className="empty"><h2>No renovation types yet</h2><p>Add a renovation type to start organizing creatives.</p></div></td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
        {state.status === 'loading' ? <div className="management-empty" role="status">Loading renovation types…</div> : null}
        {state.status === 'error' ? (
          <div className="management-error" role="alert">
            {catalogErrorMessage(state.error, 'Renovation types could not be loaded.')} <button type="button" onClick={load}>Retry</button>
          </div>
        ) : null}
      </div>
      <div className="sr-only" role="status" aria-live="polite">{announcement}</div>
      <div className="notice" style={{ marginTop: 20 }}>New types appear in the creative editor. Customers see a type once it has eligible published creatives.</div>
      {dialogElement}
    </>
  );
}
